<template>
  <div>
    <div class="row g-3 mb-3">
      <div class="col-md-8">
        <label class="form-label small fw-semibold">Nom du fournisseur *</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.name"
          required
        />
      </div>
      <div class="col-md-4">
        <label class="form-label small fw-semibold">Contact principal</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.contact_name"
          placeholder="Nom de la personne de contact"
        />
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-4">
        <label class="form-label small fw-semibold">Téléphone</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.phone"
          type="tel"
          placeholder="+241..."
          @input="sanitizePhoneField"
        />
      </div>
      <div class="col-md-4">
        <label class="form-label small fw-semibold">Email</label>
        <PrimevueInputText
          type="email"
          class="w-100"
          v-model="form.email"
          placeholder="contact@fournisseur.com"
        />
      </div>
      <div class="col-md-4">
        <label class="form-label small fw-semibold">Ville</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.city"
          placeholder="Libreville, Port-Gentil..."
        />
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-8">
        <label class="form-label small fw-semibold">Adresse</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.address"
          placeholder="Adresse complète"
        />
      </div>
      <div class="col-md-4">
        <label class="form-label small fw-semibold">N° fiscal / RCCM</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.tax_number"
          placeholder="RC..."
        />
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label small fw-semibold">Actif</label>
      <div class="form-check d-flex align-items-center gap-2 ps-0">
        <PrimevueCheckbox v-model="form.is_active" binary inputId="supplierActive" />
        <label class="form-check-label small" for="supplierActive">
          Disponible pour les commandes d'achats
        </label>
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label small fw-semibold">Notes internes</label>
      <PrimevueTextarea
        class="w-100"
        rows="2"
        v-model="form.notes"
        autoResize
        placeholder="Conditions particulières, délais, remarques..."
      />
    </div>

    <div class="d-flex justify-content-end gap-2 mt-3">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="submitting"
        @click="$emit('cancel')"
      >
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
      </button>
      <AppLoadingButton
        type="button"
        class="btn btn-primary btn-sm"
        :loading="submitting"
        loading-label="Enregistrement..."
        @click="submit"
      >
        <i class="fa fa-save me-1"></i>
        Enregistrer
      </AppLoadingButton>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { isPhoneInputValid, phoneInputValidationMessage, sanitizePhoneInput } from '@/utils/phone';

interface SupplierFormData {
  id?: number;
  name: string;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  address: string | null;
  tax_number: string | null;
  is_active: boolean;
  notes: string | null;
}

export default defineComponent({
  name: 'SupplierForm',
  props: {
    mode: {
      type: String as PropType<'create' | 'edit'>,
      required: true,
    },
    supplier: {
      type: Object as PropType<Partial<SupplierFormData>>,
      default: () => ({}),
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        id: undefined,
        name: '',
        contact_name: '',
        phone: '',
        email: '',
        city: '',
        address: '',
        tax_number: '',
        is_active: true,
        notes: '',
      } as SupplierFormData,
    };
  },
  mounted() {
    this.initForm();
  },
  methods: {
    initForm() {
      if (this.mode === 'edit' && this.supplier && this.supplier.id) {
        this.form.id = this.supplier.id;
        this.form.name = this.supplier.name || '';
        this.form.contact_name = this.supplier.contact_name || '';
        this.form.phone = this.supplier.phone || '';
        this.form.email = this.supplier.email || '';
        this.form.city = this.supplier.city || '';
        this.form.address = this.supplier.address || '';
        this.form.tax_number = this.supplier.tax_number || '';
        this.form.is_active = this.supplier.is_active ?? true;
        this.form.notes = this.supplier.notes || '';
      } else {
        this.form = {
          id: undefined,
          name: '',
          contact_name: '',
          phone: '',
          email: '',
          city: '',
          address: '',
          tax_number: '',
          is_active: true,
          notes: '',
        };
      }
    },

    sanitizePhoneField(event: Event) {
      this.form.phone = sanitizePhoneInput((event.target as HTMLInputElement | null)?.value ?? this.form.phone);
    },

    submit() {
      if (this.submitting) return;

      if (!this.form.name.trim()) {
        alert('Le nom du fournisseur est obligatoire.');
        return;
      }

      if (!isPhoneInputValid(this.form.phone)) {
        alert(phoneInputValidationMessage('téléphone du fournisseur'));
        return;
      }

      const payload = {
        id: this.form.id,
        name: this.form.name.trim(),
        contact_name: this.form.contact_name || null,
        phone: sanitizePhoneInput(this.form.phone) || null,
        email: this.form.email || null,
        city: this.form.city || null,
        address: this.form.address || null,
        tax_number: this.form.tax_number || null,
        is_active: this.form.is_active,
        notes: this.form.notes || null,
      };

      this.$emit('save', payload);
    },
  },
});
</script>
